import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileDown } from "lucide-react";

import { DataTableNew } from "@/components/shared/DataTable";
import { Tag } from "@/components/shared/Tag";
import StatusBadge from "@/components/shared/StatusBadge";
import CopyId from "@/components/ui/CopyId";
import JsonViewer from "@/pages/super-admin/audit-logs/components/JsonViewer";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { formatDateWithUserTimeZone } from "@/utils/dateUtils";
import { RecordService } from "@/services/recordService";

// `from` and `to` are JSONB on a history row (`old_value` / `new_value`) and a
// plain string on an emails row. Neither is a timestamp, so neither goes near
// the date formatter — `new Date({})` throws and the cell falls back to
// `[object Object]`.
const JSON_KEYS = new Set(["from", "to"]);

const formatTimestamp = (value) => {
  if (value == null || value === "") return "—";
  try {
    return formatDateWithUserTimeZone(value, "dd MMM yyyy HH:mm");
  } catch {
    return String(value);
  }
};

const humanise = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

// The union of every row's keys, in first-seen order, the same rule
// `workbook.columns_of` uses on the export side. Sections like `identity` and
// `bulk_intake` concatenate two queries with different shapes, so the first
// row alone is not the column set.
const columnsOf = (rows = []) => {
  const seen = [];
  for (const row of rows) {
    for (const key of Object.keys(row ?? {})) {
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen;
};

// The first id-shaped key a row carries. Sections do not share one. Falls back
// to the row's position, which is stable within a page.
const ID_KEYS = ["id", "sessionId", "roundId", "itemId", "applicationId", "candidateId", "code"];
const rowKeyOf = (row, index) => {
  for (const key of ID_KEYS) {
    if (row?.[key]) return String(row[key]);
  }
  return `row-${index}`;
};

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const JsonCell = ({ value }) => (
  <div className="max-w-[480px]">
    <JsonViewer value={value} />
  </div>
);

/**
 * Builds the columns for one section page from the keys of every row on it.
 * Every section returns a different row shape — this is the whole table
 * configuration, plus the special cases below. Nothing else.
 */
const buildColumns = ({ columnKeys, rows, sectionKey, basePath, canExport, onExportPdf, exportingDecision }) => {
  if (!columnKeys?.length) return [];

  return columnKeys.map((columnKey) => {
    const lower = columnKey.toLowerCase();

    // AI decisions — the row's action is Export just this: one section, which
    // downloads as a bundle. The transcript PDFs exist only inside it; there is
    // no endpoint that serves one on its own.
    if (sectionKey === "ai_decisions" && lower === "runid" && canExport && onExportPdf) {
      return {
        key: columnKey,
        label: "Decision",
        sortable: false,
        render: (val, r) => (
          <span className="flex items-center gap-2">
            <CopyId label="Run" value={val} />
            <Button
              type="button"
              variant="ghost"
              disabled={exportingDecision}
              onClick={(e) => {
                e.stopPropagation();
                onExportPdf(r);
              }}
              aria-label="Download this AI decision as a bundle"
              title="Export just this decision"
              className="text-text-muted hover:text-text-primary rounded-4xl"
            >
              <FileDown size={13} aria-hidden="true" />
            </Button>
          </span>
        ),
      };
    }

    // The `impact` section answers "which candidates did this employee
    // affect" — the reason the user side exists. The candidate column links
    // into that candidate's dossier.
    if (sectionKey === "impact" && lower.includes("candidate") && lower.endsWith("id")) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) =>
          val ? (
            <span className="flex items-center gap-2">
              <Link to={`${basePath}?q=${encodeURIComponent(val)}&type=candidate`} className="text-text-blue hover:underline text-[11px] font-mono">
                Open
              </Link>
              <CopyId label="Candidate" value={val} />
            </span>
          ) : (
            "—"
          ),
      };
    }

    if (lower === "deleted") {
      return {
        key: columnKey,
        label: "Deleted",
        sortable: false,
        align: "center",
        render: (val) => <StatusBadge isActive={!val} isDeleted={Boolean(val)} size="sm" />,
      };
    }

    if (["status", "kind", "matchedon", "matchedby", "servedfromcache"].includes(lower)) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) =>
          val == null || val === "" ? "—" : <Tag text={String(typeof val === "object" && !Array.isArray(val) ? JSON.stringify(val) : val)} color="gray" />,
      };
    }

    if (JSON_KEYS.has(lower)) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) => <JsonCell value={val} />,
      };
    }

    if (lower === "at" || lower.endsWith("_at") || /[a-z]At$/.test(columnKey)) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        wrap: true,
        render: (val) => <span>{formatTimestamp(val)}</span>,
      };
    }

    if (["context", "extractedfields"].includes(lower)) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) => <JsonCell value={val} />,
      };
    }

    if (lower.endsWith("id")) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) => <CopyId label={humanise(columnKey)} value={val} />,
      };
    }

    if (rows.some((r) => isObject(r?.[columnKey]))) {
      return {
        key: columnKey,
        label: humanise(columnKey),
        sortable: false,
        render: (val) => <JsonCell value={val} />,
      };
    }

    return {
      key: columnKey,
      label: humanise(columnKey),
      sortable: false,
      render: (val) => (
        <span className="block max-w-[320px] truncate" title={val == null ? undefined : String(val)}>
          {val == null || val === "" ? "—" : String(val)}
        </span>
      ),
    };
  });
};

/**
 * One dossier section, paged. The `note` comes from the section response and
 * is written for the end user — rendered verbatim under the heading.
 */
const SectionTable = ({
  subjectType,
  subjectId,
  sectionKey,
  page = 1,
  perPage = 25,
  onPageChange,
  onPerPageChange,
  applicationId,
  tenantId,
  basePath,
  canExport = false,
  onExportPdf,
  exportingDecision = false,
  onHiddenApplicationCount,
}) => {
  const [sectionData, setSectionData] = useState({
    note: "",
    payloadTruncated: false,
    total: 0,
    hasMore: false,
    rows: [],
  });

  // The response the DataTable fetch lands is stashed here, then surfaced
  // through state — the note, the truncation flag and the rows (whose keys
  // define the columns) all come out of the same call.
  const onResponse = useCallback(
    (data) => {
      setSectionData({
        note: data?.note ?? "",
        payloadTruncated: !!data?.payloadTruncated,
        total: data?.total ?? 0,
        hasMore: !!data?.hasMore,
        rows: Array.isArray(data?.rows) ? data.rows : [],
      });

      // Every section response carries the hidden-application count too, and
      // it is fresher than the one the dossier index returned on load.
      onHiddenApplicationCount?.(data?.hiddenApplicationCount ?? 0);
    },
    [onHiddenApplicationCount],
  );

  const api = useCallback(
    async (args) => {
      const data = await RecordService.getSection({
        subjectType,
        subjectId,
        section: sectionKey,
        page: args.page ?? 1,
        perPage: args.per_page ?? perPage,
        applicationId,
        tenantId,
      });
      onResponse(data);
      return {
        data: {
          rows: data?.rows ?? [],
          pagination: {
            total: data?.total ?? 0,
            page: data?.page ?? 1,
            per_page: data?.perPage ?? perPage,
            total_pages: Math.ceil((data?.total ?? 0) / (data?.perPage ?? perPage)),
          },
        },
      };
    },
    [subjectType, subjectId, sectionKey, applicationId, tenantId, perPage, onResponse],
  );

  useEffect(() => {
    setSectionData({ note: "", payloadTruncated: false, total: 0, hasMore: false, rows: [] });
  }, [subjectId, sectionKey]);

  const columns = useMemo(
    () =>
      buildColumns({
        columnKeys: columnsOf(sectionData.rows),
        rows: sectionData.rows ?? [],
        sectionKey,
        basePath,
        canExport,
        onExportPdf,
        exportingDecision,
      }),
    [sectionData.rows, sectionKey, basePath, canExport, onExportPdf, exportingDecision],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* The note is written for the end user — render it verbatim. */}
      {sectionData.note && (
        <p className="rounded-lg border border-border-primary bg-background-tertiary/60 px-3.5 py-2.5 text-[12px] leading-5 text-text-secondary">{sectionData.note}</p>
      )}

      <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-border-primary bg-background-secondary">
        <DataTableNew
          key={`${subjectId}-${sectionKey}`}
          api={api}
          dataPath="rows"
          columns={columns}
          sortable={false}
          rowKey={rowKeyOf}
          showPagination={true}
          paginationLabel="rows"
          itemSize={perPage}
          initialPage={page}
          onPageChange={onPageChange}
          onPageSizeChange={onPerPageChange}
        />
      </div>

      {sectionData.payloadTruncated && (
        <div className="flex items-start gap-2 rounded-lg border border-tone-amber/30 bg-tone-amber-soft px-3.5 py-2.5">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-tone-amber" aria-hidden="true" />
          <p className="text-[12px] leading-5 text-text-primary">Some values are too long to display. The complete text is in the exported bundle.</p>
        </div>
      )}
    </div>
  );
};

export default SectionTable;
